import { useId } from '@floating-ui/react';
import { Button } from '@hologram-dimension/button';
import { DropdownPanel, DropdownSection } from '@hologram-dimension/dropdown-panel';
import { Icon } from '@hologram-dimension/icon';
import { Link } from '@hologram-dimension/link';
import { Panel } from '@hologram-dimension/panel';
import { Popover } from '@hologram-dimension/popover';
import { clsx } from 'clsx/lite';
import Table from 'components/Table/Table';
import type { HeaderItem, TableCellItem, TableRowItem } from 'components/Table/types';
import { Workbook } from 'exceljs';
import { DateTime } from 'luxon';
import { unparse } from 'papaparse';
import { useCallback, useMemo, useState, type FC, type ReactNode } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import useOrganization from 'state/contexts/Organization/useOrganization';
import { EnumSimInventoryExportType, exportTypeToLabel } from 'types/common';
import type { LegacyUsageDataRecord } from 'types/legacyDashboard';
import getUserFriendlyBytes from 'utils/bytes/getUserFriendlyBytes';
import getNormalizedRelativeDuration from 'utils/dates/getNormalizedRelativeDuration';
import downloadCSV from 'utils/files/downloadCSV';
import downloadJSON from 'utils/files/downloadJSON';
import downloadXLSX from 'utils/files/downloadXLSX';
import useSimUrl from 'utils/hooks/useSimUrl';
import EnumSimPage from 'utils/legacy/paths/simPage';
import useOrgUrl from 'utils/legacy/useOrgUrl';
import formatNumber from 'utils/numbers/formatNumber';
import { useUserInfo } from 'hooks';
import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import { InlineNotification } from '@hologram-dimension/inline-notification';
import { useSimDetails, useSimStatuses } from 'api/index';
import { apiDateToDateTime } from 'utils/dates';
import { EnumsSimNetworkState } from 'types/__generated__/SimInventoryApi';
import styles from './SimSessionsPanel.module.css';

interface SimSessionsPanelProps {
  mode: 'sim' | 'organization';
  hologramSimId?: HologramSimId;
  usageData: Array<LegacyUsageDataRecord>;
  onLoadMoreClick: AnyFunction;
  loading: boolean;
  hasMore: boolean;
  simDetailBaseUrl: string;
  pageSize: number;
  usageByRecentSessionUrl: string;
}

const headers: Array<HeaderItem> = [
  { children: 'End time (UTC)', key: 'endtime' },
  { children: 'Start time (UTC)', key: 'starttime' },
  { children: 'Duration', key: 'duration' },
  { children: 'SIM name', key: 'simname' },
  { children: 'Data usage', key: 'datausage', className: styles.rightAlign },
  { children: 'Profile ICCID', key: 'profileiccid' },
  { children: 'IMEI', key: 'imei' },
  { children: 'Network', key: 'network' },
  { children: 'RAT', key: 'rat' },
];

const SimSessionsPopover: FC<{ children: ReactNode; content: ReactNode; className?: string }> = ({
  children,
  content,
  className,
}) => {
  const popoverId = useId();
  return (
    <Popover
      id={popoverId!}
      content={content}
      className={clsx(styles.popover, className)}
      allowPinning={false}
    >
      {children}
    </Popover>
  );
};

const Timestamp: FC<{ timestamp: DateTime; children?: ReactNode }> = ({ timestamp, children }) => {
  if (!timestamp.isValid) {
    return children;
  }

  return (
    <SimSessionsPopover
      className={styles.numeric}
      content={
        <div className={clsx(styles.popoverContent, styles.numeric)}>
          {timestamp.toLocal().toFormat('MM-dd-yy ttt')}
          <br />
          {getNormalizedRelativeDuration({
            start: timestamp,
            top: 2,
          })}
        </div>
      }
    >
      {children}
    </SimSessionsPopover>
  );
};

const SimName: FC<{ hologramSimId?: HologramSimId; simName: string; simDetailBaseUrl: string }> = ({
  hologramSimId,
  simName,
  simDetailBaseUrl,
}) => {
  const organizationId = useOrganization();
  const url = useSimUrl({
    simDetailBaseUrl,
    orgId: organizationId,
    simId: hologramSimId,
    subpage: EnumSimPage.recentSessions,
  });

  return <Link to={url!}>{simName}</Link>;
};

const SimSessionsPanel: FC<SimSessionsPanelProps> = ({
  mode,
  hologramSimId,
  usageData,
  onLoadMoreClick,
  loading: parentLoading,
  hasMore,
  simDetailBaseUrl,
  pageSize,
  usageByRecentSessionUrl,
}) => {
  const organizationId = useOrganization();
  const url = useSimUrl({ simDetailBaseUrl, orgId: organizationId, simId: hologramSimId });
  const usageByRecentSessionsActual = useOrgUrl({
    baseUrl: usageByRecentSessionUrl,
    orgId: organizationId,
  });
  const { simDetails, isLoading: isLoadingSimDetails } = useSimDetails(hologramSimId);
  const { activationTime, networkStatus } = simDetails;
  const { networkStatusGroups, isLoading: isLoadingSimStatuses } = useSimStatuses();
  const isPredeployment = networkStatus
    ? networkStatusGroups?.predeployment?.includes(networkStatus as EnumsSimNetworkState)
    : false;
  const activationDateTime = activationTime ? apiDateToDateTime(activationTime) : undefined;
  const { releaseFlags } = useUserInfo();
  const hasPredeployment = releaseFlags.has(ReleaseFlag.FEATURE_PREDEPLOYMENT);

  const loading = parentLoading || isLoadingSimDetails || isLoadingSimStatuses;

  const normalizedUsageData = useMemo(
    () =>
      usageData.map(
        ({
          record_id: recordId,
          session_begin: sessionBegin,
          timestamp: sessionEnd,
          is_reporting_window_with_usage: isReportingWindow,
          devicename: simName,
          bytes: dataUsageBytes,
          sim: iccid,
          linkid: profileId,
          deviceid: deviceId,
          imei,
          network_name: carrier,
          radio_access_technology: rat,
          simcardid,
        }) => {
          const start = DateTime.fromFormat(sessionBegin, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' });
          const end = DateTime.fromFormat(sessionEnd, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' });

          let duration;
          if (start?.isValid && end?.isValid) {
            duration = end.diff(start, 'seconds');
          }

          const durationText = duration?.isValid ? duration.toFormat('hh:mm:ss') : undefined;

          return {
            recordId,
            sessionEnd,
            sessionBegin,
            end,
            start,
            duration: durationText,
            simName,
            isReportingWindow,
            dataUsageBytes,
            profileIccid: iccid,
            profileId,
            deviceId,
            imei,
            network: carrier,
            rat,
            hologramSimId: simcardid,
          };
        }
      ),
    [usageData]
  );

  const rows = useMemo<Array<TableRowItem>>(() => {
    let useDarkerColor = false;
    const lastProfileForSim = new Map<HologramSimId, ProfileId>();
    let lastSessionTestMode: boolean = false;
    const testModeMessageRow: TableRowItem = {
      key: 'test-mode-message',
      cells: [
        {
          key: 'test-mode-message',
          children: (
            <InlineNotification
              prefixOverride="Test Mode"
              variant="note"
              iconOverride="TestMode"
              className={styles.testModeInlineNotification}
            >
              This SIM was activated at{' '}
              <span className={styles.numeric}>
                {activationDateTime?.toFormat('yyyy-MM-dd HH:mm:ss')} UTC.
              </span>{' '}
              Sessions that opened before that time were free sessions included in Test Mode.
            </InlineNotification>
          ),
          colSpan: headers.length,
        },
      ],
      className: clsx(styles.row, styles.testModeMessageRow),
    };

    const rowsToReturn: Array<TableRowItem> = [];
    // Detect profile changes in this set
    const copyOfNormalizedUsageData = [...normalizedUsageData];
    copyOfNormalizedUsageData
      .reverse()
      .forEach(
        ({
          recordId,
          profileId,
          end,
          sessionEnd,
          start,
          sessionBegin,
          duration,
          isReportingWindow,
          simName,
          dataUsageBytes,
          profileIccid,
          imei,
          network,
          rat,
          hologramSimId: hologramSimIdForRow,
        }) => {
          const cells: Array<TableCellItem> = new Array(headers.length);

          // test to see if this is a test mode session
          // Either the sim is currently in test mode,
          // or the session start time is before the sim activation time
          const isTestModeSession =
            isPredeployment ||
            !!(hasPredeployment && activationDateTime && start < activationDateTime);

          let profileChanged = false;
          if (lastProfileForSim.has(hologramSimIdForRow)) {
            if (lastProfileForSim.get(hologramSimIdForRow) !== profileId) {
              profileChanged = true;
              lastProfileForSim.set(hologramSimIdForRow, profileId);
            }
          } else {
            lastProfileForSim.set(hologramSimIdForRow, profileId);
          }

          if (profileChanged) {
            useDarkerColor = !useDarkerColor;
          }

          let columnNum = 0;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: (
              <div className={styles.iconWrapper}>
                {isTestModeSession && <Icon name="TestMode" fill="DdsColorIconDefault" />}
                <Timestamp timestamp={end}>{sessionEnd}</Timestamp>
              </div>
            ),
            className: clsx(styles.cell, styles.numeric),
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: <Timestamp timestamp={start}>{sessionBegin}</Timestamp>,
            className: clsx(styles.cell, styles.numeric),
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: (
              <>
                {duration && <div className={styles.numeric}>{duration}</div>}
                {isReportingWindow === 1 && (
                  <div className={styles.textAndPopover}>
                    <span className={styles.reportingWindow}>Reporting window</span>
                    <SimSessionsPopover content="Represents total usage in the time frame. Reporting times on this network may vary.">
                      <Icon
                        name="CircleInfo"
                        size="small"
                        fill="DdsColorInteractivePrimaryDefault"
                      />
                    </SimSessionsPopover>
                  </div>
                )}
              </>
            ),
            className: clsx(styles.cell, styles.duration),
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: (
              <SimName
                hologramSimId={hologramSimIdForRow}
                simName={simName}
                simDetailBaseUrl={simDetailBaseUrl}
              />
            ),
            className: styles.cell,
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: (
              <SimSessionsPopover
                content={getUserFriendlyBytes(dataUsageBytes, 'bytes')}
                className={styles.numeric}
              >
                {getUserFriendlyBytes(dataUsageBytes)}
              </SimSessionsPopover>
            ),
            className: clsx(styles.cell, styles.rightAlign),
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: (
              <div className={clsx(styles.textAndPopover, styles.profileChanged)}>
                <span className={styles.numeric}>{profileIccid}</span>
                {profileChanged && (
                  <SimSessionsPopover content="Enabled profile changed. This could be from a fallback event or a manual profile switch.">
                    <Icon
                      name="SwapSIM"
                      fill={useDarkerColor ? 'DdsColorPaletteTeal40' : 'DdsColorPaletteTeal30'}
                    />
                  </SimSessionsPopover>
                )}
              </div>
            ),
            className: styles.cell,
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: imei,
            className: clsx(styles.cell, styles.numeric),
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: network,
            className: styles.cell,
          };

          columnNum += 1;
          cells[columnNum] = {
            key: headers[columnNum].key,
            children: rat,
            className: styles.cell,
          };

          // Optionally, add the test mode message row to the rowsToReturn array
          if (!isPredeployment && !isTestModeSession && lastSessionTestMode) {
            rowsToReturn.push(testModeMessageRow);
          }

          // Add the session data row to the rowsToReturn array
          rowsToReturn.push({
            key: `${recordId}`,
            cells,
            className: clsx(styles.row, isTestModeSession && styles.testModeRow),
          });

          lastSessionTestMode = isTestModeSession;
        }
      );

    // If SIM is currently in predeployment, add a row to the top of the table
    if (isPredeployment) {
      rowsToReturn.push({
        key: 'predeployment-message',
        cells: [
          {
            key: 'predeployment-message',
            children: (
              <InlineNotification
                prefixOverride="Test Mode"
                variant="note"
                iconOverride="TestMode"
                className={styles.testModeInlineNotification}
              >
                This SIM is configured with pre-deployment testing and has not yet been activated.
              </InlineNotification>
            ),
            colSpan: headers.length,
          },
        ],
        className: clsx(styles.row, styles.testModeMessageRow),
      });
    }

    // Show the test mode message row if the SIM is not in Test Mode but the last session was in test mode
    if (!isPredeployment && lastSessionTestMode) {
      rowsToReturn.push(testModeMessageRow);
    }

    return rowsToReturn.reverse();
  }, [
    normalizedUsageData,
    isPredeployment,
    hasPredeployment,
    activationDateTime,
    simDetailBaseUrl,
  ]);

  const [showExport, setShowExport] = useState(false);
  const onExportClick = useCallback(() => {
    setShowExport((prev) => !prev);
  }, []);
  const exportButtonId = useId();

  const { sendAnalyticsEvent, sendAnalyticsError } = useAnalytics();
  const [processingExport, setProcessingExport] = useState(false);

  const exportSessions = useCallback(
    async (exportType: EnumSimInventoryExportType) => {
      setShowExport(false);

      const exportTypeLabel = exportTypeToLabel(exportType);
      try {
        setProcessingExport(true);

        // Need to remove and rename some rows
        const formattedUsageData = normalizedUsageData.map(
          ({
            sessionEnd,
            sessionBegin,
            duration,
            isReportingWindow,
            dataUsageBytes,
            profileIccid,
            profileId,
            deviceId,
            imei,
            network,
            rat,
          }) => ({
            endTimeUTC: sessionEnd,
            startTimeUTC: sessionBegin,
            duration,
            isReportingWindow,
            dataUsageBytes,
            profileIccid,
            profileId,
            deviceId,
            imei,
            network,
            rat,
          })
        );

        const now = DateTime.now();
        const timestamp = now.toFormat('yyyyLLdd-hhmmssSSS');

        switch (exportType) {
          case EnumSimInventoryExportType.XLSX:
          case EnumSimInventoryExportType.CSV:
            if (exportType === EnumSimInventoryExportType.XLSX) {
              const workbook = new Workbook();
              workbook.creator = 'Hologram';
              workbook.lastModifiedBy = 'Hologram';

              const nowJS = now.toJSDate();
              workbook.created = nowJS;
              workbook.modified = nowJS;

              const sheet = workbook.addWorksheet('SIMs');
              sheet.addRow(Object.keys(formattedUsageData[0]));
              sheet.addRows(formattedUsageData.map((record) => Object.values(record)));

              const buffer = await workbook.xlsx.writeBuffer();

              downloadXLSX({
                data: buffer as any,
                filename: `Sessions-${timestamp}.xlsx`,
              });
            } else {
              downloadCSV({
                data: unparse(formattedUsageData),
                filename: `Sessions-${timestamp}.csv`,
              });
            }

            break;
          case EnumSimInventoryExportType.JSON:
            downloadJSON({
              data: JSON.stringify(formattedUsageData, undefined, 1),
              filename: `Sessions-${timestamp}.json`,
            });
            break;
          default:
            break;
        }

        setProcessingExport(false);
      } catch (e) {
        if (sendAnalyticsError) {
          sendAnalyticsError('Export SIMs error', e as any);
        }
      }

      if (sendAnalyticsEvent) {
        sendAnalyticsEvent(`Recent sessions - Export Sessions - Export Sessions success`, {
          exportType: exportTypeLabel,
        });
      }
    },
    [normalizedUsageData, sendAnalyticsError, sendAnalyticsEvent]
  );

  const content = useMemo(() => {
    const newContent: Array<DropdownSection> = [];

    newContent.push({
      label: `Export ${formatNumber(usageData.length)} sessions`,
      items: [
        {
          children: 'Export to XLSX (Excel)',
          iconStart: 'DownloadTray',
          onClick: () => exportSessions(EnumSimInventoryExportType.XLSX),
        },
        {
          children: 'Export to CSV',
          iconStart: 'DownloadTray',
          onClick: () => exportSessions(EnumSimInventoryExportType.CSV),
        },
        {
          children: 'Export to JSON',
          iconStart: 'DownloadTray',
          onClick: () => exportSessions(EnumSimInventoryExportType.JSON),
        },
      ],
    });

    return newContent;
  }, [exportSessions, usageData]);

  return (
    <Panel
      isLoading={loading}
      className={styles.panel}
      noContentPadding
      header="Recent session details"
      headerActions={
        <DropdownPanel
          content={content}
          open={showExport}
          onOpenChange={setShowExport}
          className={styles.exportDropdown}
        >
          <Button
            iconEnd={showExport ? 'ChevronSingleNorth' : 'ChevronSingleSouth'}
            variant="secondary"
            onClick={onExportClick}
            disabled={loading}
            id={exportButtonId}
            loading={processingExport}
          >
            Export
          </Button>
        </DropdownPanel>
      }
      footerVariant="alternative"
      footerActions={
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <Button variant="secondary" onClick={onLoadMoreClick} disabled={loading || !hasMore}>
              {`Load ${formatNumber(pageSize)} more sessions`}
            </Button>
            <span className={styles.footerLeftText}>
              Showing last {formatNumber(usageData.length)} closed data sessions
            </span>
          </div>
          <div className={styles.footerRight}>
            <Button
              iconEnd="ArrowEastShort"
              variant="tertiary"
              to={mode === 'organization' ? usageByRecentSessionsActual! : `${url}/usage`}
            >
              View data usage history
            </Button>
          </div>
        </div>
      }
    >
      <Table headers={headers} rows={rows} wrapperClassName={styles.tableWrapper} />
    </Panel>
  );
};

export default SimSessionsPanel;
